import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CustomerCard from "./CustomerCard";
import { fetchCustomersForProvider } from "../../services/userManagementService";

const CustomerSelector = ({ onCustomerSelect, theme }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      // Get customers from the current user's perspective (provider)
      const customerList = await fetchCustomersForProvider(theme);
      setCustomers(customerList);
      setError(null);
    } catch (err) {
      console.error("Error fetching customers:", err);
      setError("Failed to load customers. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const handleCustomerClick = (customerId) => {
    const customer = customers.find((c) => c.id === customerId);
    if (customer) {
      setSelectedCustomer(customerId);
      onCustomerSelect(customer);
    }
  };

  const handleEditCustomer = (customer) => {
    // The Edit User button will open the UserDialog
    // For now, we just call onCustomerSelect which includes the customer data
    // The actual edit functionality will be handled by the parent component
    onCustomerSelect(customer);
  };

  const handleClearSelection = () => {
    setSelectedCustomer(null);
    onCustomerSelect(null);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress size={60} sx={{ color: "#24e4ff" }} />
        <Typography sx={{ mt: 3, color: theme.palette.text.primary }}>
          Loading customers...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (customers.length === 0) {
    return (
      <Card sx={{ bgcolor: theme.palette.background.paper, mb: 2 }}>
        <CardContent>
          <Typography
            variant="h6"
            sx={{ mb: 2, color: theme.palette.text.primary }}
          >
            No Customers Found
          </Typography>
          <Typography sx={{ color: theme.palette.text.secondary, mb: 2 }}>
            You don't have any customers yet. Customers need to be created by
            your provider account.
          </Typography>
          <Button
            variant="contained"
            onClick={handleClearSelection}
            sx={{
              bgcolor: theme.palette.primary.main,
              "&:hover": {
                bgcolor: theme.palette.primary.dark,
              },
            }}
          >
            Return to Invoice Creation
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          color: theme.palette.text.primary,
          fontWeight: 600,
          mb: 2,
        }}
      >
        Select a Customer
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.secondary,
          mb: 3,
        }}
      >
        Choose a customer from the list below to create an invoice
      </Typography>

      <Grid container spacing={3}>
        {customers.map((customer) => (
          <Grid item xs={12} sm={6} md={4} key={customer.id}>
            <CustomerCard
              customer={customer}
              selected={selectedCustomer === customer.id}
              onSelect={handleCustomerClick}
              onEdit={handleEditCustomer}
              theme={theme}
            />
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="outlined"
          onClick={handleClearSelection}
          sx={{
            borderColor: theme.palette.divider,
            color: theme.palette.text.secondary,
            "&:hover": {
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
            },
          }}
        >
          Clear Selection
        </Button>
      </Box>
    </Box>
  );
};

export default CustomerSelector;
